import { geocodeByPlaceId } from 'react-google-places-autocomplete';

// Thin wrappers around the Google Maps Places JS API so individual screens can
// embed predictions inside their own UI instead of using the high-level
// `<PlacesAutocomplete>` component.

export type PlacePrediction = {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
};

export type PlaceAddress = {
  address_line1: string;
  city: string;
  state: string;
  zip: string;
};

const getMaps = () =>
  typeof window !== 'undefined' ? window.google?.maps : undefined;

let autocompleteService: google.maps.places.AutocompleteService | null = null;
const getAutocompleteService = () => {
  const maps = getMaps();
  if (!maps?.places) return null;
  if (!autocompleteService) {
    autocompleteService = new maps.places.AutocompleteService();
  }
  return autocompleteService;
};

export const searchPlaces = (input: string): Promise<PlacePrediction[]> => {
  const service = getAutocompleteService();
  const trimmed = input.trim();
  if (!service || trimmed.length < 2) return Promise.resolve([]);
  return new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input: trimmed,
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      (predictions, status) => {
        const maps = getMaps();
        const okStatus = maps?.places?.PlacesServiceStatus.OK;
        if (status !== okStatus || !predictions) {
          resolve([]);
          return;
        }
        resolve(
          predictions.map((p) => ({
            place_id: p.place_id,
            description: p.description,
            main_text: p.structured_formatting?.main_text ?? p.description,
            secondary_text: p.structured_formatting?.secondary_text ?? '',
          })),
        );
      },
    );
  });
};

const findComponent = (
  result: google.maps.GeocoderResult,
  type: string,
  short = false,
) => {
  const match = result.address_components.find((component) =>
    component.types.includes(type),
  );
  if (!match) return '';
  return short ? match.short_name : match.long_name;
};

export const getPlaceAddress = async (
  placeId: string,
): Promise<PlaceAddress | null> => {
  const results = await geocodeByPlaceId(placeId);
  const result = results[0];
  if (!result) return null;
  const streetNumber = findComponent(result, 'street_number');
  const route = findComponent(result, 'route');
  const city =
    findComponent(result, 'locality') ||
    findComponent(result, 'sublocality') ||
    findComponent(result, 'sublocality_level_1') ||
    findComponent(result, 'postal_town') ||
    findComponent(result, 'administrative_area_level_2');
  const state = findComponent(result, 'administrative_area_level_1', true);
  const zip = findComponent(result, 'postal_code');
  return {
    address_line1: [streetNumber, route].filter(Boolean).join(' '),
    city,
    state,
    zip,
  };
};
